import React, { BaseSyntheticEvent, useEffect } from 'react';
import { CQAppConstant } from '../../../../app-constants';
import { ISubHeading } from '../../../../common/cq-inputs/cq-combobox/cq-combobox';
import { Utility } from '../../../../common/utility';
import FieldAndOperator from '../../../../container/FieldAndOperator/FieldAndOperator';
import { CQAppStore } from '../../../../store/cq-app-store';
import { CQMasterDataStore } from '../../../../store/cq-master-data.store';
import LayoutFieldsConfigController from '../../layout-level-fields.controller';
import { ICoreRule, ICriteria, IExtendedRule } from '../ILayoutConfigurationPanel.Interface';
import { LayoutConfigRootComponent } from '../../layout-level-fields/layout-config-root-component';

type IProps = {
	selectedRule?: string; // only assign if you want the resulted rule in [selectedRule]: [{...}] format
	templateName: string;
	objectName?: string;
	parentAction: {
		updateState: Function;
		updateExtraState?: Function;
	};
	parentInfo: {
		state: ICoreRule[] | ICoreRule;
		extraState?: IExtendedRule;
		sectionIndex?: number;
		initialCriteria: ICriteria;
		addFieldOnSkip: boolean;
	};
	modalInfo: {
		shouldRenderAddComplexBtn: boolean;
		shouldDisableFirstElement: boolean;
		toggleModalVisibility: Function;
		optionInfo?: {
            options: any[];
            subHeading?: ISubHeading[]
        };
		hideSkipBtn?: boolean 
	};
};

const utility = new Utility();
const layoutFieldsConfigCtrl = new LayoutFieldsConfigController();

export const CommonRuleModal = (props: IProps) => {
	const {
		modalInfo: { toggleModalVisibility, shouldDisableFirstElement, shouldRenderAddComplexBtn, optionInfo, hideSkipBtn },
		templateName,
		objectName,
		parentInfo: { extraState, state, initialCriteria, sectionIndex , addFieldOnSkip },
		parentAction: { updateExtraState },
		selectedRule
	} = props;

	/** 
	 *@description gets refernce field or picklist field if field type selected
	 */
	useEffect(() => {
		if (Array.isArray(state)) {
			for(let i=0;i<state[0].addedCriterias.length;i++){
				getReferenceOrPicklistFields(state[0].addedCriterias[i]);	
			} 
		} else {
			for(let i=0;i<state.addedCriterias.length;i++){
				getReferenceOrPicklistFields(state.addedCriterias[i]);
			} 
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state]);

	/**
	 * @description checks whether the data is present in state
	 */
	function isSectionIndexPresent() {
		return sectionIndex !== undefined;
	}

	/**
	 * @description Gets the state data
	 */
	function getSectionData(): ICoreRule {
		const stateCopy = utility.createObjectDeepCopy(state);
		if (isSectionIndexPresent() && Array.isArray(stateCopy)) {
			return stateCopy[sectionIndex!];
		} else {
			return stateCopy as ICoreRule;
		}
	}

	/**
	 * @description Updates the state property for the rule 
	 * @param sectionState property required for rule
	 */
	function updateState(sectionState: ICoreRule) {
		let stateCopy = utility.createObjectDeepCopy(state);

		if (sectionIndex !== undefined && Array.isArray(stateCopy)) {
			stateCopy[sectionIndex] = sectionState;
		} else {
			stateCopy = sectionState;
		}
		if (selectedRule) {
			props.parentAction.updateState({ [selectedRule]: stateCopy });
		} else {
			props.parentAction.updateState(stateCopy);
		}
	}

	/**
	 * @description Toggles the fields and operator of given section
	 */
	function shouldSkipBtn() {
		const sectionState = getSectionData();
		sectionState.addedCriterias = [{...initialCriteria}];
		sectionState.complexLogicQuery = '1';
		sectionState.complexLogicVisibility = false;
		sectionState.shouldSkip = !sectionState.shouldSkip;
		if(sectionState.addedCriterias[0] && !sectionState.addedCriterias[0].field && sectionState.shouldSkip===true && addFieldOnSkip){
			if(templateName !== 'cqNBA'){
				const selectedField = { ...CQMasterDataStore.Details.selectedFieldDetailsObject };
				sectionState.addedCriterias[0].field = selectedField.fullName;	
			}
		}

		updateState(sectionState);
	}

	/**
	 * @description selects field for criteria
	 * @param e event to add fields to criteria
	 * @param criteriaIndex index of  criteria where field is added
	 */
	async function onFieldChanged(e: BaseSyntheticEvent, criteriaIndex: number) {
		const sectionState = getSectionData();

		let relationField: string = '';

		let fieldType = sectionState.addedCriterias[criteriaIndex].selectedFieldType;
		let referenceField = sectionState.addedCriterias[criteriaIndex].referenceField;
		let relationshipName = sectionState.addedCriterias[criteriaIndex].relationshipName;

		const selectedIndex = e.target.options.selectedIndex;
        const itemType = e.target.options[selectedIndex].getAttribute('itemType');

		if (itemType){
			sectionState.addedCriterias[criteriaIndex].fieldType= itemType
		}

		if (CQAppConstant.REFERENCE_FIELD_TYPE.includes(fieldType) && referenceField) {
			relationField = relationshipName + '.' + referenceField;
			updateAdditionalFields(relationField, CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.DELETE, criteriaIndex);
		}

		sectionState.addedCriterias[criteriaIndex].compareTo = '';
		sectionState.addedCriterias[criteriaIndex].relationshipName = '';
		const selectedField = layoutFieldsConfigCtrl.getFieldRelatedMetadata(
			templateName,
			e.target.value.split('-')[1] === 'customSettings' ? 'customSettings' : e.target.value,
			templateName.includes('cqGrid') ? itemType : undefined
		);
		if (selectedField.fieldType === CQAppConstant.PICKLIST) {
			addPickListFieldToState(e.target.value);
		}
		sectionState.addedCriterias[criteriaIndex].selectedFieldType = selectedField.fieldType;

		// If field in the form '<objectApiName>-*', to get only objectApiName
		const replaceFieldName = sectionState.addedCriterias[criteriaIndex].field.split('-')[0];
		sectionState.addedCriterias[criteriaIndex].field = e.target.value;
		const additionalFieldAction =
			replaceFieldName === ''
				? CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.ADD
				: CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.UPDATE;

		if (CQAppConstant.REFERENCE_FIELD_TYPE.includes(selectedField.fieldType)) {
			sectionState.addedCriterias[criteriaIndex].referenceTo = selectedField.referenceTo;
			sectionState.addedCriterias[criteriaIndex].relationshipName = selectedField.relationshipName;
			setReferenceFieldData(selectedField.referenceTo, selectedField.fieldFullName);
		}

		updateState(sectionState);

		// If field in the form '<objectApiName>-*', to get only objectApiName
		const field = sectionState.addedCriterias[criteriaIndex].field.split('-')[0];
		await updateAdditionalFields(field, additionalFieldAction, criteriaIndex, replaceFieldName);
	}

	/**
	 * @description Adds fields to be compareto
	 * @param e event that adds compared field
	 * @param criteriaIndex index of  where compare field needs to be added
	 */
	function onCompareFieldChanged(e: BaseSyntheticEvent, criteriaIndex: number) {
		const sectionState = getSectionData();
		// If field in the form '<objectApiName>-fieldName', to get only objectApiName
		const selectedIndex = e.target.options.selectedIndex;
        const itemType = e.target.options[selectedIndex].getAttribute('itemType');

		if (itemType){
			sectionState.addedCriterias[criteriaIndex].compareFieldType = itemType;
		}
		const replaceFieldName = sectionState.addedCriterias[criteriaIndex].compareTo.split('-')[0];
		sectionState.addedCriterias[criteriaIndex].compareTo = e.target.value;
		const additionalFieldAction =
			replaceFieldName === ''
				? CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.ADD
				: CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.UPDATE;
		updateState(sectionState);

		// If field in the form '<objectApiName>-fieldName', to get only objectApiName
		const fieldName = sectionState.addedCriterias[criteriaIndex].compareTo.split('-')[0];
		updateAdditionalFields(fieldName, additionalFieldAction, criteriaIndex, replaceFieldName);
	}

	/**
	 * @description Adds reference field to the selected field
	 * @param e event that adds reference field
	 * @param criteriaIndex index from which criteria need to be deleted
	 */
	function onReferenceFieldChanged(e: BaseSyntheticEvent, criteriaIndex: number) {
		const sectionState = getSectionData();
		let fieldToBeReplaced = '';

		if (sectionState.addedCriterias[criteriaIndex].referenceField) {
			fieldToBeReplaced =
				sectionState.addedCriterias[criteriaIndex].relationshipName +
				'.' +
				sectionState.addedCriterias[criteriaIndex].referenceField;
		}
		const additionalFieldAction =
			sectionState.addedCriterias[criteriaIndex].referenceField === ''
				? CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.ADD
				: CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.UPDATE;
		sectionState.addedCriterias[criteriaIndex].referenceField = e.target.value;
		let newField =
			sectionState.addedCriterias[criteriaIndex].relationshipName +
			'.' +
			sectionState.addedCriterias[criteriaIndex].referenceField;
		updateState(sectionState);
		updateAdditionalFields(newField, additionalFieldAction, criteriaIndex, fieldToBeReplaced);
	}

	/**
	 * @description Adds operator for the critria
	 * @param e event that adds operator
	 * @param criteriaIndex index for critira where operator needs to be added
	 */
	function onOperatorChanged(e: BaseSyntheticEvent, criteriaIndex: number) {
		const sectionState = getSectionData();
		sectionState.addedCriterias[criteriaIndex].operator = e.target.value;
		sectionState.addedCriterias[criteriaIndex].compareTo =
			sectionState.addedCriterias[criteriaIndex].operator === 'Exists' ||
			sectionState.addedCriterias[criteriaIndex].operator === 'IsEmpty'
				? ''
				: sectionState.addedCriterias[criteriaIndex].compareTo;

		updateState(sectionState);
	}

	/**
	 * @description Adds text field if comparision field is text
	 * @param compareToValue value for text field added
	 * @param criteriaIndex index of criteria where compareto text must be added
	 */
	function onCompareToTextChanged(compareToValue: string, criteriaIndex: number) {
		const sectionState = getSectionData();
		sectionState.addedCriterias[criteriaIndex].compareTo = compareToValue;
		updateState(sectionState);
	}

	/**
	 * @description Updates the logic query for given index
	 * @param e value to which state need to be updated
	 * @param selectionIndex which index logic is updated
	 */
	function onComplexLogicQueryChanged(e: BaseSyntheticEvent) {
		const sectionState = getSectionData();

		sectionState.complexLogicQuery = e.target.value;
		updateState(sectionState);
	}

	/**
	 * @description Updates toggle action for dynamic field
	 * @param e value to identify if action if dynamic or static
	 * @param criteriaIndex index of criteria to add dynamic fields
	 */
	function onDynamicFieldsChanged(e: BaseSyntheticEvent, criteriaIndex: number) {
		const sectionState = getSectionData();
		sectionState.addedCriterias[criteriaIndex].isDynamic = e.target.checked;

		updateState(sectionState);
	}

	/**
	 * @description Added initial criteria in given index
	 * @param index index in which new criteria needs to be added
	 */
	function addCriteria() {
		const sectionState = getSectionData();

		sectionState.addedCriterias.push(initialCriteria);
		sectionState.complexLogicQuery = generateComplexLogic(sectionState);

		updateState(sectionState);
	}

	/**
	 * @description Deletes the criteria from given section and index of state and updated additional field accordingly
	 * @param criteriaIndex index from which criteria need to be deleted
	 */
	function deleteCriteria(criteriaIndex: number) {
		const sectionState = getSectionData();
		let addedCriterias = sectionState.addedCriterias;

		updateAdditionalFields(addedCriterias[criteriaIndex].field, CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.DELETE, criteriaIndex);
		if (addedCriterias[criteriaIndex].isDynamic) {
			updateAdditionalFields(
				addedCriterias[criteriaIndex].compareTo,
				CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.DELETE,
				criteriaIndex
			);
		}

		if (CQAppConstant.REFERENCE_FIELD_TYPE.includes(addedCriterias[criteriaIndex].selectedFieldType)) {
			//removing reference field with <objectApiName>.<field>
			const referenceValue =
				addedCriterias[criteriaIndex].relationshipName + '.' + addedCriterias[criteriaIndex].referenceField;
			updateAdditionalFields(referenceValue, CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.DELETE, criteriaIndex);
		}

		addedCriterias = addedCriterias.filter((criteria: ICriteria, index: number) => index !== criteriaIndex);

		sectionState.addedCriterias = addedCriterias;
		sectionState.complexLogicQuery = generateComplexLogic(sectionState);

		updateState(sectionState);
	}

	/**
	 * @description Generates complex logic query in accordance to the sectionIndex of given criteria
	 * @returns generated complex logic query
	 */
	function generateComplexLogic(state: ICoreRule) {
		let complexLogicQuery = '';
		state.addedCriterias.forEach((criteria: ICriteria, index: number) => {
			complexLogicQuery += state.addedCriterias.length - 1 === index ? `${index + 1}` : `${index + 1} `;
			if (state.addedCriterias.length - 1 !== index) {
				complexLogicQuery += CQAppConstant.AND_OPERATOR + ' ';
			}
		});
		return complexLogicQuery;
	}

	/**
	 * @description Makes complex logic field visible
	 */
	function showComplexLogicInput() {
		const sectionState = getSectionData();
		sectionState.complexLogicVisibility = true;
		updateState(sectionState);
	}
	
	/**
	 * @description Checks whether the field should be added in additinalfields or not
	 * @param addedCriteria criteria property of field selected
	 * @param criteriaIndex index of the the criteria added
	 */
	function isAdditionalFieldExists (addedCriteria: { field:string,compareTo: string; }[], criteriaIndex : number)  {
        if (addedCriteria[criteriaIndex]){
            const replaceFieldName = addedCriteria[criteriaIndex].compareTo.includes('-')
                                    ? addedCriteria[criteriaIndex].compareTo.split('-')[1]
                                    : addedCriteria[criteriaIndex].field.split('-')[1]
            if(CQAppConstant.CUSTOM_ADDED_FIELD.includes(replaceFieldName)){
                return true;
            } else {
                return false;
            }
        }
    }	

	/**
	 * @description updates the additional fields for the template
	 * @param fieldNameToAdd field that needs to be added in additional field
	 * @param action action that need to be performed
	 * @param fieldNameToRemove field that needs to be replace or removed from additional field
	 */
	async function updateAdditionalFields(fieldNameToAdd: string, action: string, criteriaIndex: number,fieldNameToRemove?: string,) {
		const layoutAdditionalFields = new LayoutFieldsConfigController().getAdditionalFields();
		let layoutAdditionalFieldExists = false;
		layoutAdditionalFields.forEach(field => {
			if(fieldNameToAdd.includes(field.fieldFullName) && field.customAddedField){
				layoutAdditionalFieldExists = true;
			}
		})
		const additionalFields = utility.createObjectDeepCopy(extraState?.additionalFields);
		if(!additionalFields || !updateExtraState || layoutAdditionalFieldExists) return;

		const sectionState = getSectionData();
		let addedCriterias = sectionState.addedCriterias;
		const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
		const isAdditionalFieldExist = layout && layout.layout && Object.keys(layout.layout).length !== 0 ? 
									   layout.fields.some((layoutField: string) => layoutField === fieldNameToAdd): isAdditionalFieldExists(addedCriterias,criteriaIndex);
		if (action === CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.ADD && !isAdditionalFieldExist) {
			additionalFields.push(fieldNameToAdd);
		} else if (action === CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.DELETE) {
			const deleteItemIndex = additionalFields.indexOf(fieldNameToAdd);
			if (deleteItemIndex > -1) {
				additionalFields.splice(deleteItemIndex, 1);
			}
		} else if (action === CQAppConstant.ADDITIONAL_FIELDS_ACTIONS.UPDATE && fieldNameToRemove) {
			const updateItemIndex = additionalFields.indexOf(fieldNameToRemove);
			if (updateItemIndex > -1) {
				additionalFields.splice(updateItemIndex, 1);
			}
			if (!isAdditionalFieldExist) {
				additionalFields.push(fieldNameToAdd);
			}
		}
		updateExtraState({ ...extraState, additionalFields});
	}

	/**
	 * @description generates picklist or reference value of given selected field of criteria
	 * @param addedCriteria criteria whose selected field's picklist or reference value need to be calculated
	 */
	function getReferenceOrPicklistFields(addedCriteria: ICriteria) {
		const fieldType = addedCriteria.selectedFieldType;
		if (fieldType === CQAppConstant.PICKLIST) {
			addPickListFieldToState(addedCriteria.field);
		} else if (CQAppConstant.REFERENCE_FIELD_TYPE.includes(fieldType)) {
			setReferenceFieldData(addedCriteria.referenceTo, addedCriteria.field);
		}
	}

	/**
	 * @description picklist field is added to the state
	 * @param fieldName name of the object
	 */
	async function addPickListFieldToState(fieldName: string) {
		const objName = CQMasterDataStore.OutputConfig.getStoreKeyData(templateName, 'selectedObject');

		const picklistValuesCopy = utility.createObjectDeepCopy(extraState?.fieldWisePicklistValues);

		if(!picklistValuesCopy || !updateExtraState) return;

		const isPicklistFieldExist = picklistValuesCopy.some((field: any) => field.fieldFullName === fieldName);

		if (!isPicklistFieldExist) {
			CQAppStore.loader.setVisibility(true);
			const picklistValues = await layoutFieldsConfigCtrl.getPicklistValues(objName, fieldName);
			CQAppStore.loader.setVisibility(false);

			if (picklistValues) {
				const picklistField = { fieldFullName: fieldName, pickListValues: picklistValues };
				picklistValuesCopy.push(picklistField);
				updateExtraState({ ...extraState, fieldWisePicklistValues: picklistValuesCopy });
			}
		}

		return picklistValuesCopy;
	}

	/**
	 * @description Renders reference field drop down if present
	 * @param criteria contains property for field selected
	 * @param criteriaIndex  index of criterias added
	 */
	function renderReferenceFieldsDropDown(criteria: ICriteria, criteriaIndex: number) {
		const referenceFieldCopy = utility.createObjectDeepCopy(extraState?.selectedReferenceFields);

		if(!referenceFieldCopy) return;

		const selectedField = referenceFieldCopy.filter((selectedField: any) => {
			return selectedField.fieldName === criteria.field;
		});

		return (
			<>
				{selectedField.length > 0 && (
					<div className='slds-form-element'>
						<div className='slds-form-element__control'>
							<div className='slds-form-element__control'>
								<div className='slds-select_container'>
									<select
										className='slds-select'
										role="select-reference-field" 
										id='select-01'
										value={criteria.referenceField}
										onChange={(e) => onReferenceFieldChanged(e, criteriaIndex)}
									>
										<option value=''>Select reference field</option>
										{selectedField[0].relatedFields && selectedField[0].relatedFields.map((field: any) => (
											<option key={field.name} value={field.name}>
												{field.name}
											</option>
										))}
									</select>
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	/**
	 * @description get the reference field if not present
	 * @param refernceFieldName name of the reference field
	 * @param fieldName name of the object
	 */
	async function setReferenceFieldData(refernceFieldName: string, fieldName: string) {
		const referenceFieldsCopy = utility.createObjectDeepCopy(extraState?.selectedReferenceFields);
		if(!referenceFieldsCopy || !updateExtraState) return;
		const hasReferenceFieldDetails = referenceFieldsCopy.some(
			(selectedReferenceField: any) => selectedReferenceField.fieldName === fieldName
		);

		if (!hasReferenceFieldDetails) {
			CQAppStore.loader.setVisibility(true);
			layoutFieldsConfigCtrl
				.getLookupFields(refernceFieldName)
				.then(async(lookupFields: any) => {
					if (fieldName === 'customSettings') {
						lookupFields.fields = lookupFields.fields.filter((field: any) => field.name.includes('__c'));
						fieldName = objectName + '-' + fieldName;
					}
					referenceFieldsCopy.push({
						fieldName,
						refernceFieldName,
						relatedFields: lookupFields.fields,
					});
					await updateExtraState({ ...extraState, selectedReferenceFields: referenceFieldsCopy });
					CQAppStore.loader.setVisibility(false);
				})
				.catch((error : any) => {
					toggleModalVisibility();
					CQAppStore.loader.setVisibility(false);
					const errorObj = {
						errorType: CQAppConstant.ERROR_CUSTOM,
						errorMessage: error.error ? error.error : error.message,
						errorStatus: error.status,
						componentName: '',
					};
					CQAppStore.error.dispatchError(errorObj);
				});
		}
	}

	let stateCopy = getSectionData();
	const optionsInfo = optionInfo || { options : layoutFieldsConfigCtrl.getLayoutFields(templateName)}
	return (
		<FieldAndOperator
			hideSkipBtn={hideSkipBtn || false}
			shouldRender={stateCopy.shouldSkip}
			setValueSectionIndex={sectionIndex}
			values={stateCopy}
			shouldSkipBtn={shouldSkipBtn}
			optionInfo ={optionsInfo}
			onFieldChanged={onFieldChanged}
			selectedReferenceFields={extraState?.selectedReferenceFields}
			renderReferenceFieldsDropDown={renderReferenceFieldsDropDown}
			onOperatorChanged={onOperatorChanged}
			onCompareToTextChanged={onCompareToTextChanged}
			layoutFieldsConfigCtrl={layoutFieldsConfigCtrl}
			fieldWisePicklistValues={extraState?.fieldWisePicklistValues}
			onCompareFieldChanged={onCompareFieldChanged}
			onDynamicFieldsChanged={onDynamicFieldsChanged}
			addCriteria={addCriteria}
			deleteCriteria={deleteCriteria}
			onComplexLogicQueryChanged={onComplexLogicQueryChanged}
			showComplexLogicInput={showComplexLogicInput}
			shouldRenderAddComplexBtn={shouldRenderAddComplexBtn}
			shouldDisableFirstElement={shouldDisableFirstElement}
			objectName={objectName}
		/>
	);
};
